import React, { useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { DynamicBooksService } from '../services/PaiPlatformService';
import './BuildBookModal.css';

const BuildBookModal = ({ isOpen, onClose, onSuccess }) => {
  const isAuthenticated = useIsAuthenticated();
  const [formData, setFormData] = useState({
    bookName: '',
    bookAuthor: '',
    bookPersona: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.bookName.trim()) {
      setError('Book name is required');
      return;
    }
    
    if (!formData.bookAuthor.trim()) {
      setError('Author name is required');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    setResult(null);
    
    try {
      // Call the orchestrator API which runs AI agents to build the book
      const orchestratorResult = await DynamicBooksService.orchestrateBook({
        bookName: formData.bookName.trim(),
        bookAuthor: formData.bookAuthor.trim(),
        bookPersona: formData.bookPersona.trim() || 'A knowledgeable reader seeking insights',
      });
      
      // console.log('BookOrchestrator response:', JSON.stringify(orchestratorResult, null, 2));
      
      // Store the result to display
      setResult(orchestratorResult);
      
      // Helper to get property (handles both PascalCase and camelCase)
      const getProp = (obj, ...keys) => {
        for (const key of keys) {
          if (obj[key] !== undefined) return obj[key];
        }
        return undefined;
      };
      
      // If orchestration was successful and we have a blobFolder, create the DynaBook record
      const blobFolder = getProp(orchestratorResult, 'BlobFolder', 'blobFolder');
      if (blobFolder) {
        try {
          // Extract infographic URL from the response if available
          const agents = getProp(orchestratorResult, 'Agents', 'agents') || [];
          const infographicAgent = agents.find(
            a => (a.AgentId || a.agentId)?.toLowerCase().includes('infographic')
          );
          const infographicUrl = getProp(infographicAgent || {}, 'BlobUrl', 'blobUrl') || getProp(orchestratorResult, 'BlobFolderUrl', 'blobFolderUrl');

          // Create the DynaBook database record
          const createdBook = await DynamicBooksService.createBook({
            bookName: getProp(orchestratorResult, 'BookTitle', 'bookTitle'),
            bookAuthor: getProp(orchestratorResult, 'BookAuthor', 'bookAuthor'),
            bookPersona: formData.bookPersona.trim() || 'A knowledgeable reader seeking insights',
            infographicUrl: infographicUrl,
            storageFolder: blobFolder,
          });
          
          // console.log('DynaBook created successfully:', createdBook);
          
          // Update with token usage data
          if (createdBook?.id) {
            try {
              const agentTokenUsage = agents.map(agent => ({
                agentId: getProp(agent, 'AgentId', 'agentId'),
                success: getProp(agent, 'Success', 'success'),
                duration: getProp(agent, 'Duration', 'duration'),
                tokensUsed: getProp(agent, 'TokensUsed', 'tokensUsed'),
              }));

              await DynamicBooksService.updateBook(createdBook.id, {
                totalTokensUsed: getProp(orchestratorResult, 'TotalTokens', 'totalTokens'),
                totalDuration: getProp(orchestratorResult, 'TotalDuration', 'totalDuration'),
                agentTokenUsage: agentTokenUsage,
              });
              //console.log('DynaBook updated with token usage data');
            } catch (updateError) {
              console.error('Failed to update DynaBook with token usage:', updateError);
            }
          }
        } catch (saveError) {
          console.error('Failed to save DynaBook record:', saveError);
          // Don't fail the whole operation if saving record fails - orchestration succeeded
        }
      }
    } catch (err) {
      console.error('Failed to orchestrate book:', err);
      setError(err.message || 'Failed to build book. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    // Reset form
    setFormData({
      bookName: '',
      bookAuthor: '',
      bookPersona: '',
    });
    setResult(null);
    setError(null);
    
    // Notify parent of success
    if (onSuccess) {
      onSuccess(result);
    }
    
    onClose();
  };

  const handleBackdropClick = (e) => {
    // Don't close while submitting
    if (isSubmitting) return;
    if (e.target === e.currentTarget) {
      if (result) {
        handleDone();
      } else {
        onClose();
      }
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    if (result) {
      handleDone();
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="build-book-modal">
        <div className="modal-header">
          <div className="modal-title-section">
            <h2 className="modal-title">📚 Build Book</h2>
            <p className="modal-subtitle">
              {result ? `Results for "${result.BookTitle || result.bookTitle || result.title || formData.bookName || 'Book'}"` : 'Create a new AI-powered book analysis'}
            </p>
          </div>
          <button 
            className="modal-close-btn"
            onClick={handleClose}
            disabled={isSubmitting}
            title="Close"
          >
            ×
          </button>
        </div>
        
        <div className="modal-body">
          {!isAuthenticated && !result && (
            <div className="auth-warning">
              <span className="warning-icon">⚠️</span>
              <span>Please sign in to build a book</span>
            </div>
          )}
          
          {error && (
            <div className="form-error">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Loading State */}
          {isSubmitting && (
            <div className="loading-state">
              <div className="loading-spinner-large"></div>
              <h3 className="loading-title">Building your book...</h3>
              <p className="loading-subtitle">
                AI agents are analyzing "{formData.bookName}" by {formData.bookAuthor}. 
                This typically takes 1-2 minutes.
              </p>
              <div className="loading-agents">
                <p>Generating:</p>
                <ul>
                  <li>📝 Summary &amp; Key Insights</li>
                  <li>🔑 Keywords &amp; Concepts</li>
                  <li>❓ Quiz Questions</li>
                  <li>🎯 Action Items</li>
                  <li>🖼️ Infographic</li>
                  <li>💡 Expert Insights</li>
                </ul>
              </div>
            </div>
          )}

          {/* Results Display */}
          {result && !isSubmitting && (
            <div className="results-container">
              <div className="results-summary">
                <div className="result-stat">
                  <span className="stat-label">Total time:</span>
                  <span className="stat-value">{(((result.TotalDuration || result.totalDuration || result.duration || 0) / 1000) / 60).toFixed(1)} minutes</span>
                </div>
                <div className="result-stat">
                  <span className="stat-label">Tokens used:</span>
                  <span className="stat-value">
                    {(result.TotalTokens || result.totalTokens || result.tokensUsed || (result.Agents || result.agents)?.reduce((sum, a) => sum + (a.TokensUsed || a.tokensUsed || 0), 0) || 0).toLocaleString()}
                  </span>
                </div>
              </div>

              {(result.Agents || result.agents) && (result.Agents || result.agents).length > 0 ? (
                <div className="agent-results">
                  <h4 className="agent-results-title">Agent Results:</h4>
                  <ul className="agent-list">
                    {(result.Agents || result.agents).map((agent) => (
                      <li key={agent.AgentId || agent.agentId} className="agent-item">
                        <span className={`agent-status ${(agent.Success || agent.success) ? 'success' : 'failure'}`}>
                          {(agent.Success || agent.success) ? '✓' : '✗'}
                        </span>
                        <span className="agent-name">{agent.AgentId || agent.agentId}</span>
                        {(agent.TokensUsed || agent.tokensUsed) > 0 && (
                          <span className="agent-tokens">({(agent.TokensUsed || agent.tokensUsed).toLocaleString()} tokens)</span>
                        )}
                        {(agent.BlobUrl || agent.blobUrl) && (
                          <a
                            href={agent.BlobUrl || agent.blobUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="agent-view-link"
                          >
                            (view)
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="agent-results">
                  <h4 className="agent-results-title">Response:</h4>
                  <pre className="debug-response" style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#f3f4f6', padding: '8px', borderRadius: '4px', maxHeight: '200px', overflow: 'auto' }}>
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Form - hidden when showing results or loading */}
          {!result && !isSubmitting && (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="bookName" className="form-label">
                  Book Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="bookName"
                  name="bookName"
                  className="form-input"
                  placeholder="e.g., Atomic Habits"
                  value={formData.bookName}
                  onChange={handleChange}
                  disabled={!isAuthenticated}
                  autoFocus
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="bookAuthor" className="form-label">
                  Author <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="bookAuthor"
                  name="bookAuthor"
                  className="form-input"
                  placeholder="e.g., James Clear"
                  value={formData.bookAuthor}
                  onChange={handleChange}
                  disabled={!isAuthenticated}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="bookPersona" className="form-label">
                  Persona
                  <span className="optional-hint">(optional)</span>
                </label>
                <textarea
                  id="bookPersona"
                  name="bookPersona"
                  className="form-input form-textarea"
                  placeholder="e.g., A business professional looking to improve productivity and build better habits..."
                  value={formData.bookPersona}
                  onChange={handleChange}
                  disabled={!isAuthenticated}
                  rows={3}
                />
                <p className="form-hint">
                  Describe who you are and what you're hoping to learn from this book. 
                  This helps tailor the AI analysis to your needs.
                </p>
              </div>
            </form>
          )}
        </div>
        
        <div className="modal-footer">
          {result ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleDone}
            >
              ✓ Done - Close &amp; Refresh
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isSubmitting || !isAuthenticated || !formData.bookName.trim() || !formData.bookAuthor.trim()}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Analyzing... (1-2 min)
                  </>
                ) : (
                  <>
                    🚀 Build Book
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildBookModal;
